<template>
  <div class="service-wrap pt-20 pb-20">
    <div class="container">
      <!-- En-tête -->
      <div class="row mb-5">
        <div class="col-12 text-center">
          <h2 class="section-title">Nos Formations</h2>
          <p class="section-subtitle">Découvrez nos différentes filières et niveaux d'études</p>
        </div>
      </div>

      <!-- Message de chargement -->
      <div v-if="isLoading" class="row">
        <div class="col-12 text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Chargement...</span>
          </div>
          <p class="mt-3">Chargement des formations...</p>
        </div>
      </div>

      <!-- Grille des formations -->
      <div v-else class="row">
        <div 
          class="col-xl-3 col-lg-4 col-md-6 mb-4" 
          v-for="(item, index) in filiereNiveaux" 
          :key="index"
        >
          <div class="formation-card">
            <div class="formation-icon-wrapper">
              <i class="flaticon-education formation-icon"></i>
            </div>
            <div class="formation-content">
              <h5 class="formation-title">{{ item.filiere?.libelle || 'Non spécifié' }}</h5>
              <div class="formation-niveau">
                <i class="flaticon-level"></i>
                <span>{{ item.niveau?.libelle || 'Non spécifié' }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Message si aucune donnée -->
        <div v-if="!isLoading && filiereNiveaux.length === 0" class="col-12 text-center py-5">
          <div class="no-data">
            <i class="flaticon-search"></i>
            <h4>Aucune formation disponible</h4>
            <p>Il n'y a pas de formation à afficher pour le moment.</p>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div class="mt-5" v-if="totalPages > 1">
        <PaginationComponent
          :page="page"
          :totalPages="totalPages"
          :totalElements="totalElements"
          :limit="limit"
          @paginate="handlePaginate"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import ApiService from "@/services/ApiService";
import { error } from "@/utils/utils";
import { defineComponent, ref, onMounted } from "vue";
import PaginationComponent from "@/components/Utilities/Pagination.vue";

interface Filiere {
  id: number;
  code: string;
  libelle: string;
}

interface Niveau {
  id: number;
  code: string;
  libelle: string;
}

interface Matiere {
  id: number;
  code: string;
  libelle: string;
}

interface FiliereNiveauMatiere {
  id: number;
  filiere?: Filiere;
  niveau?: Niveau;
  matiere?: Matiere;
  coefficient?: number;
  statut: number;
  createdAt?: string;
  updatedAt?: string;
}

export default defineComponent({
  name: "FormaTions",
  components: { PaginationComponent },
  setup() {
    const filiereNiveaux = ref<Array<FiliereNiveauMatiere>>([]);
    const isLoading = ref(false);

    // Pagination
    const page = ref(1);
    const totalPages = ref(0);
    const limit = ref(12);
    const totalElements = ref(0);

    const handlePaginate = ({ page_, limit_ }) => {
      page.value = page_;
      getFiliereNiveaux(page_, limit_);
    };

    // Charger les filières avec niveaux
    function getFiliereNiveaux(pageNum = 1, limi = 12) {
      isLoading.value = false;
      return ApiService.get(`/filiereNiveauMatieres?page=${pageNum}&limit=${limi}`)
        .then(({ data }) => {
          console.log('Données reçues:', data);
          filiereNiveaux.value = data.data.data || [];
          totalPages.value = data.data.totalPages || 0;
          limit.value = data.data.limit || 12;
          totalElements.value = data.data.totalElements || 0;
        })
        .catch(({ response }) => {
          console.error('Erreur:', response);
          error(response?.data?.message || "Erreur lors du chargement des formations");
          filiereNiveaux.value = [];
        })
        .finally(() => {
          // isLoading.value = false;
        });
    }

    onMounted(() => {
      getFiliereNiveaux();
    });

    return {
      filiereNiveaux,
      isLoading,
      page,
      totalPages,
      limit,
      totalElements,
      handlePaginate
    };
  },
});
</script>

<style scoped>
.service-wrap {
  background-color: #f8f9fa;
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 1rem;
}

.section-subtitle {
  font-size: 1.1rem;
  color: #6c757d;
  margin-bottom: 2rem;
}

.formation-card {
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
  cursor: pointer;
}

.formation-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.formation-icon-wrapper {
  background: linear-gradient(135deg, #0b1b3f 0%, #0b1b3f 100%);
  padding: 40px;
  text-align: center;
  position: relative;
  overflow: hidden;
}

.formation-icon-wrapper::before {
  content: '';
  position: absolute;
  top: -50%;
  right: -50%;
  width: 200%;
  height: 200%;
  background: rgba(255, 255, 255, 0.1);
  transform: rotate(45deg);
  transition: all 0.5s ease;
}

.formation-card:hover .formation-icon-wrapper::before {
  top: -100%;
  right: -100%;
}

.formation-icon {
  font-size: 64px;
  color: white;
  position: relative;
  z-index: 1;
}

.formation-content {
  padding: 25px;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.formation-title {
  font-size: 1.3rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 15px;
  line-height: 1.4;
  min-height: 56px;
}

.formation-niveau {
  display: flex;
  align-items: center;
  background: #fff8e1;
  padding: 12px 15px;
  border-radius: 8px;
  border-left: 4px solid #ffc107;
}

.formation-niveau i {
  font-size: 20px;
  color: #f9a825;
  margin-right: 10px;
}

.formation-niveau span {
  font-size: 0.95rem;
  font-weight: 600;
  color: #f57f17;
}

.no-data {
  padding: 60px 20px;
}

.no-data i {
  font-size: 80px;
  color: #d0d0d0;
  margin-bottom: 20px;
}

.no-data h4 {
  font-size: 1.5rem;
  color: #6c757d;
  margin-bottom: 10px;
}

.no-data p {
  color: #adb5bd;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

/* Responsive */
@media (max-width: 768px) {
  .section-title {
    font-size: 2rem;
  }

  .formation-title {
    font-size: 1.1rem;
    min-height: auto;
  }

  .formation-icon {
    font-size: 48px;
  }

  .formation-icon-wrapper {
    padding: 30px;
  }
}
</style>